import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SERVER_BASE_URL } from '../../../environments/environment';
import {
  CheckoutResponseUrl,
  StandardCheckoutResponse,
  UserRegistrationDto,
} from '../user-service/user.service';
import { Observable } from 'rxjs/internal/Observable';
import { Currency } from '@app/model/currency';
import { LoanOptionVM } from '@app/route/shared/LoanOptionVM';
import { LoanOption } from '@app/route/shared/LoanOption';
import { BaseStateLocaleCode } from '@app/service/state/state.service';
import { FinsharkResult } from '../credit/credit.service';
import { KalpAnswersFI, KalpInfoReason } from '../leq/leq.service';

@Injectable({
  providedIn: 'root',
})
export class FiUserService {
  private readonly httpReqOptions = { withCredentials: true };

  constructor(private http: HttpClient) {}

  initAuth(callbackUrl: string): Promise<AuthUrlResponse> {
    return this.http
      .post<AuthUrlResponse>(
        `${SERVER_BASE_URL}/user/fi/scrive/initAuth/`,
        {
          callbackUrl,
        },
        this.httpReqOptions
      )
      .toPromise();
  }

  authorize(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/fi/scrive/authentication/`,
        this.httpReqOptions
      )
      .toPromise();
  }

  getFinsharkUrl(): Observable<CheckoutResponseUrl> {
    return this.http.get<CheckoutResponseUrl>(
      `${SERVER_BASE_URL}/user/fi/finshark/url`,
      this.httpReqOptions
    );
  }

  postFinsharkResults(
    result: FinsharkResult
  ): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/fi/finshark/results/`,
        result,
        this.httpReqOptions
      )
      .toPromise();
  }

  getActiveUserInformation(): Promise<UserRegistrationDto> {
    return this.http
      .get<UserRegistrationDto>(
        `${SERVER_BASE_URL}/user/fi/activeUserInformation/`,
        this.httpReqOptions
      )
      .toPromise();
  }

  getKalpInfo(reason: KalpInfoReason): Observable<KalpAnswersFI> {
    let params = new HttpParams();
    params = params.append('reason', reason);

    return this.http.get<KalpAnswersFI>(
      `${SERVER_BASE_URL}/user/fi/kalp/getInfo/`,
      {
        withCredentials: true,
        params: params,
      }
    );
  }

  postKalpAnswers(
    kalpFormAnswers: KalpAnswersFI
  ): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/fi/kalp/answers/`,
        kalpFormAnswers,
        this.httpReqOptions
      )
      .toPromise();
  }

  registerEmailAndPhone(
    form: EmailAndPhone
  ): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/fi/register/emailAndPhone/`,
        form,
        this.httpReqOptions
      )
      .toPromise();
  }

  commit(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/fi/commit`,
        this.httpReqOptions
      )
      .toPromise();
  }

  selectInstallmentPlan(loanOfferId: string): Promise<String> {
    const loanOffer = {
      id: loanOfferId,
    };

    return this.http
      .post<string>(
        `${SERVER_BASE_URL}/user/fi/selectInstallmentPlan/`,
        loanOffer,
        this.httpReqOptions
      )
      .toPromise();
  }

  buildLoanTermsString(
    selectedLoanOption: LoanOptionVM,
    totalCheckoutValue: Currency,
    language: string
  ): string {
    const {
      effectiveInterest,
      nominalInterest,
      initialFee,
      monthlyFee,
      numberOfMonths,
      totalAmount,
    } = selectedLoanOption;

    if (
      this.getCurrentStateLocaleBaseCode(language) === BaseStateLocaleCode.EN
    ) {
      return `Selected credit amount ${totalCheckoutValue.amount.toLocaleString(
        language
      )} €, installment ${numberOfMonths} months. The effective interest rate
      is ${this.getFormattedInterest(
        effectiveInterest
      )}%. Variable annual interest ${this.getFormattedInterest(
        nominalInterest
      )}%, setup fee  ${initialFee.toLocaleString(
        language
      )} €, administration fee ${monthlyFee.toLocaleString(
        language
      )} €/month and monthly amount ${selectedLoanOption.monthlyCost.toLocaleString(
        language
      )} €/month total sum of payments ${totalAmount.toLocaleString(
        language
      )} €.`;
    }

    return `Valittu luottomäärä ${totalCheckoutValue.amount.toLocaleString(
      language
    )} €, maksuerät ${numberOfMonths} kuukauden ajan. Todellinen vuosikorko
     ${this.getFormattedInterest(
       effectiveInterest
     )}%. Vaihtuva vuosikorko ${this.getFormattedInterest(
      nominalInterest
    )}%, avausmaksu  ${initialFee.toLocaleString(
      language
    )} €, hallinnointimaksu ${monthlyFee.toLocaleString(
      language
    )} €/kk ja kuukausittainen maksu ${selectedLoanOption.monthlyCost.toLocaleString(
      language
    )} €/kk Luoton kokonaissumma ${totalAmount.toLocaleString(language)} €.`;
  }

  zeroInterestString(loanOption: LoanOption, language: string): string {
    if (loanOption.nominalInterest !== 0) {
      return '';
    }

    let interestText: string = '0% korko';
    if (
      this.getCurrentStateLocaleBaseCode(language) === BaseStateLocaleCode.EN
    ) {
      interestText = '0% interest';
    }

    return interestText;
  }

  buildLoanOptionTitle(loanOption: LoanOption, language: string): string {
    const monthPerText: string =
      this.getCurrentStateLocaleBaseCode(language) === BaseStateLocaleCode.EN
        ? 'month for'
        : 'kk';

    const monthsText: string =
      this.getCurrentStateLocaleBaseCode(language) === BaseStateLocaleCode.EN
        ? 'months'
        : 'kuukauden ajan';

    return `${loanOption.monthlyCost.toLocaleString(
      language
    )} €/${monthPerText} ${loanOption.numberOfMonths} ${monthsText}`;
  }

  getBulletPoints(item: LoanOption, language: string): string[] {
    const annualInterest: string =
      this.getCurrentStateLocaleBaseCode(language) === BaseStateLocaleCode.EN
        ? 'Interest'
        : 'Korko';

    return [
      `${annualInterest} ${this.getFormattedInterest(item.nominalInterest)}%`,
    ];
  }

  getFormattedInterest(interest: number): string {
    return (interest * 100).toFixed(2);
  }

  transformDtosTOVMs(
    loanOptions: LoanOption[],
    toggleModal: () => void,
    language: string
  ): LoanOptionVM[] {
    return loanOptions.map((loanOption) =>
      this.transformDtoTOVM(loanOption, toggleModal, language)
    );
  }

  transformDtoTOVM(
    loanOption: LoanOption,
    toggleModal: () => void,
    language: string
  ): LoanOptionVM {
    return {
      badgeText: this.zeroInterestString(loanOption, language),
      bulletPoints: this.getBulletPoints(loanOption, language),
      color: '#00ac84',
      link: {
        text: this.getTermsString(language),
        onClick: toggleModal,
      },
      monthlyCost: loanOption.monthlyCost,
      nominalInterest: loanOption.nominalInterest,
      numberOfMonths: loanOption.numberOfMonths,
      radioButtonColor: '#db305e',
      title: this.buildLoanOptionTitle(loanOption, language),
      termId: loanOption.termId,
      effectiveInterest: loanOption.effectiveInterest,
      initialFee: loanOption.initialFee,
      monthlyFee: loanOption.monthlyFee,
      totalAmount: loanOption.totalAmount,
      loanTermsURL: loanOption.loanTermsURL,
      secciURL: loanOption.secciURL,
    };
  }

  getCurrentStateLocaleBaseCode(stateLanguage: string): BaseStateLocaleCode {
    switch (stateLanguage) {
      case 'en-US':
      case 'en-GB':
        return BaseStateLocaleCode.EN;
      case 'fi-FI':
        return BaseStateLocaleCode.FI;
      default:
        console.error(
          'Unknown locale not supported in Zaver for business: ',
          stateLanguage
        );
        return BaseStateLocaleCode.EN;
    }
  }

  getTermsString(stateLanguage: string): string {
    let localeBaseCode = this.getCurrentStateLocaleBaseCode(stateLanguage);

    return localeBaseCode === BaseStateLocaleCode.EN
      ? 'View terms'
      : 'Näytä luottosopimus';
  }
}

export class EmailAndPhone {
  email: string;
  phoneNumber: string;
}

//SCRIVE "RAIL A"
export class AuthUrlResponse {
  accessUrl: string;
  id: string;
}
